/**
 * Velto color guide: skill levels, DUPR requirements, category colors,
 * programs and the current women's league series.
 *
 * Single source of truth for every page that shows a level or a color
 * (/levels, homepage key, open play, FAQs, leagues, the open-play blog post).
 * Source: owner's "Velto Website Color and Skill Guide (Dot Colors)", 26 Oct 2026.
 *
 * Rules from the guide:
 *  - Labels are plus-sign targets ("3.25+"), never closed ranges ("3.25–3.49").
 *  - The headline level (target) and the "DUPR requirement" (gate) are separate
 *    fields. Never show the gate as the headline, never imply the target is a gate.
 *  - Label a minimum simply "DUPR requirement" (no "doubles").
 *  - Pink and yellow are categories, not ranks. Always show the written level.
 *  - Color is never the only cue: a text label always sits beside a dot.
 */

export type LevelColor = "white" | "blue" | "green" | "purple" | "brown" | "black" | "red";
export type CategoryColor = "pink" | "yellow";
export type DotColor = LevelColor | CategoryColor;

/** Solid sRGB dot colors. The same values are CSS tokens --level-<color> in index.css. */
export const DOT_COLORS: Record<DotColor, { name: string; hex: string; rgb: string; meaning: string }> = {
  white: { name: "White", hex: "#FFFFFF", rgb: "255, 255, 255", meaning: "Beginner / Advanced Beginner" },
  blue: { name: "Blue", hex: "#2196F3", rgb: "33, 150, 243", meaning: "Developing Intermediate" },
  green: { name: "Green", hex: "#4CAF50", rgb: "76, 175, 80", meaning: "Intermediate" },
  purple: { name: "Purple", hex: "#9C27B0", rgb: "156, 39, 176", meaning: "High Intermediate" },
  brown: { name: "Brown", hex: "#795548", rgb: "121, 85, 72", meaning: "Advanced 4.0+" },
  black: { name: "Black", hex: "#212121", rgb: "33, 33, 33", meaning: "Advanced 4.25+" },
  red: { name: "Red", hex: "#F44336", rgb: "244, 67, 54", meaning: "Advanced 4.5+" },
  pink: { name: "Pink", hex: "#EC407A", rgb: "236, 64, 122", meaning: "Women's programming" },
  yellow: { name: "Yellow", hex: "#FFD600", rgb: "255, 214, 0", meaning: "All-level and specialty programming" },
};

/** Light dots that need a visible dark outline on a light page. */
export const OUTLINED_DOTS: DotColor[] = ["white", "yellow"];

export interface SkillLevel {
  color: LevelColor;
  /** URL hash on /levels, e.g. /levels#high-intermediate */
  slug: string;
  name: string;
  /** Headline target, e.g. "3.5+". null = no number. */
  target: string | null;
  /** DUPR requirement for standard regular open play. null = none. */
  gate: string | null;
  description: string;
  image: string;
  /** PodPlay open-play list for this level (owner's direct links, 27 Oct 2026). */
  eventsUrl: string;
}

/** The seven skill tiers, in order. Keep Advanced 4.0+, 4.25+, 4.5+ separate. */
/**
 * PodPlay tag values (not display names): blue's tag value is still "Low Intermediate"
 * (shown as Developing Intermediate), black is "Advanced Plus", red is "Advanced 4.5+".
 */
const PODPLAY_EVENTS_URL_BASE = "https://velto.podplay.app/community/events";

export const SKILL_LEVELS: SkillLevel[] = [
  {
    color: "white",
    slug: "beginner",
    name: "Beginner / Advanced Beginner",
    target: null,
    gate: null,
    description:
      "You know the basic rules and scoring and want to build consistency, court awareness, and confidence through organized games.",
    image: "/levels/level-beginner.webp",
    eventsUrl: `${PODPLAY_EVENTS_URL_BASE}?level=Beginner&level=Advanced+Beginner&type=Open+Play`,
  },
  {
    color: "blue",
    slug: "developing-intermediate",
    name: "Developing Intermediate",
    target: "2.75+",
    gate: null,
    description:
      "You are moving beyond advanced-beginner play, sustaining rallies, and developing purposeful serves, returns, drops, drives, and dinks.",
    image: "/levels/level-low-intermediate.webp",
    eventsUrl: `${PODPLAY_EVENTS_URL_BASE}?level=Low+Intermediate&type=Open+Play`,
  },
  {
    color: "green",
    slug: "intermediate",
    name: "Intermediate",
    target: "3.25+",
    gate: "3.0+",
    description:
      "You sustain rallies and use consistent dinks, third-shot drops, and court positioning to build points.",
    image: "/levels/level-intermediate.webp",
    eventsUrl: `${PODPLAY_EVENTS_URL_BASE}?level=Intermediate&type=Open+Play`,
  },
  {
    color: "purple",
    slug: "high-intermediate",
    name: "High Intermediate",
    target: "3.5+",
    gate: "3.3+",
    description:
      "You are comfortable with pace and strategy, using drops, resets, and coordinated court movement during competitive rallies.",
    image: "/levels/level-high-intermediate.webp",
    eventsUrl: `${PODPLAY_EVENTS_URL_BASE}?level=High+Intermediate&type=Open+Play`,
  },
  {
    color: "brown",
    slug: "advanced-4-0",
    name: "Advanced",
    target: "4.0+",
    gate: "3.8+",
    description:
      "You handle advanced pace and use resets, counters, and deliberate shot selection to construct points under pressure.",
    image: "/levels/level-advanced.webp",
    eventsUrl: `${PODPLAY_EVENTS_URL_BASE}?level=Advanced&type=Open+Play`,
  },
  {
    color: "black",
    slug: "advanced-4-25",
    name: "Advanced",
    target: "4.25+",
    gate: "4.0+",
    description:
      "You bring consistent resets, counters, drops, and strategic point construction to fast, high-level games.",
    image: "/levels/level-advanced.webp",
    eventsUrl: `${PODPLAY_EVENTS_URL_BASE}?level=Advanced+Plus&type=Open+Play`,
  },
  {
    color: "red",
    slug: "advanced-4-5",
    name: "Advanced",
    target: "4.5+",
    gate: "4.25+",
    description:
      "You are ready for the 4.5+ level of play, with reliable execution and strategic decisions in fast, demanding exchanges.",
    image: "/levels/level-advanced.webp",
    eventsUrl: `${PODPLAY_EVENTS_URL_BASE}?level=Advanced+4.5%2B&type=Open+Play`,
  },
];

/** "High Intermediate 3.5+", "Beginner / Advanced Beginner". */
export const levelLabel = (l: Pick<SkillLevel, "name" | "target">) =>
  l.target ? `${l.name} ${l.target}` : l.name;

export const DUPR_REQUIREMENT_LABEL = "DUPR requirement";

/**
 * Pink and yellow: PodPlay tag filters. Yellow uses "All Levels", which today covers the
 * socials and Youth but not Drill & Play (tagged "Drill" in PodPlay).
 */
export const CATEGORIES: {
  color: CategoryColor;
  slug: string;
  name: string;
  description: string;
  eventsUrl: string;
}[] = [
  {
    color: "pink",
    slug: "womens",
    name: "Women's programming",
    eventsUrl: `${PODPLAY_EVENTS_URL_BASE}?tag=Women`,
    description:
      "Pink identifies women's programming across multiple levels. Look at the session's written level and requirements to choose the right fit. Pink is not a rank above red.",
  },
  {
    color: "yellow",
    slug: "all-levels",
    name: "All-level and specialty programming",
    eventsUrl: `${PODPLAY_EVENTS_URL_BASE}?tag=All%20Levels`,
    description:
      "Yellow identifies all-level and specialty programming, including all-level socials, Drill & Play, and youth sessions. Follow the individual listing for audience or skill requirements.",
  },
];

/** Pink regular women's open play (not the women's league requirements). */
export const WOMENS_OPEN_PLAY: { name: string; target: string | null; gate: string | null }[] = [
  { name: "Beginner", target: null, gate: null },
  { name: "Developing Intermediate", target: "2.5+", gate: null },
  { name: "Intermediate", target: "3.0+", gate: "2.7+" },
  { name: "Advanced", target: "3.8+", gate: "3.7+" },
];

/** Program-specific exceptions to the standard gates. Display text. */
export const GATE_EXCEPTIONS: string[] = [
  "Developing Intermediate DUPR-Logged has a 2.3+ DUPR requirement; the level stays Developing Intermediate 2.75+. The women's logged version is Developing Intermediate 2.5+ with the same 2.3+ requirement.",
  "Green $5, $15, and $20 discounted sessions are Intermediate / Advanced, with no DUPR requirement.",
  "Intermediate+ Coffee on Us has a 3.0+ DUPR requirement. Advanced Beginner DUPR-Logged and Earn Your DUPR — Intermediate 3.25+ have no existing-rating requirement.",
];

export const COLOR_GUIDE_INTRO =
  "Our schedule is color coded to help you find your match. Choose your playing level, then pick the format you enjoy—from regular open play and DUPR-logged games to Coffee on Us, prizes, discounted sessions, and socials. Each listing shows its skill level, any DUPR requirement, and whether results are submitted.";

export const OPEN_PLAY_SUMMARY =
  "Open play for your level, with regular, discounted, and social sessions throughout the week. Browse the schedule for current prices and session lengths.";

export const FIRST_TIMER_NOTE =
  "New to pickleball? Start with Intro to Pickleball or Beginner Coach-Guided. Regular white open play assumes basic familiarity with the rules and scoring.";

export const PODPLAY_EVENTS_URL = PODPLAY_EVENTS_URL_BASE;
/** Intro to Pickleball and other first-timer sessions (PodPlay "Intro" tag). */
export const FIRST_TIMER_URL = `${PODPLAY_EVENTS_URL_BASE}?tag=Intro`;
/** DUPR-Logged and Earn Your DUPR sessions (PodPlay "DUPR" tag). */
const DUPR_EVENTS_URL = `${PODPLAY_EVENTS_URL_BASE}?tag=DUPR`;

export interface Program {
  key: string;
  name: string;
  copy: string;
  /** Owner: show only formats with current availability. Set from the live PodPlay schedule. */
  active: boolean;
  /**
   * Colors this program currently runs in, from the live PodPlay schedule (27 Oct 2026).
   * A level tab only lists programs that include its color. Update when the schedule changes.
   */
  colors: DotColor[];
  url: string;
}

/** Color identifies the skill group or audience; the program label describes the format. */
export const PROGRAMS: Program[] = [
  {
    key: "regular-open-play",
    name: "Regular open play",
    copy: "Sign up individually and rotate through games with players in your session's skill group. Choose your level using the color guide.",
    active: true,
    colors: ["white", "blue", "green", "purple", "brown", "black", "red", "pink"],
    url: PODPLAY_EVENTS_URL,
  },
  {
    key: "dupr-logged",
    name: "DUPR Logged",
    copy: "Play organized games with results submitted to DUPR. Connect your DUPR account before joining; any minimum rating is listed on the session.",
    active: true,
    colors: ["white", "blue", "green", "purple", "pink"],
    url: DUPR_EVENTS_URL,
  },
  {
    key: "earn-your-dupr",
    name: "Earn Your DUPR",
    copy: "Start building a rating through recorded games. Choose a Beginner / Advanced Beginner or Intermediate session. No existing DUPR rating is required.",
    active: true,
    colors: ["white", "green"],
    url: DUPR_EVENTS_URL,
  },
  {
    key: "prizes",
    name: "Open play with prizes",
    copy: "Enjoy organized, competitive open play with prizes. Check the session listing for the prize details and any DUPR requirement.",
    active: true,
    colors: ["white", "blue", "green", "brown"],
    url: PODPLAY_EVENTS_URL,
  },
  {
    key: "coffee-on-us",
    name: "Coffee on Us",
    copy: "Start your day with pickleball, a complimentary coffee, and time to meet other players. Choose Beginner / Advanced Beginner, Developing Intermediate, or Intermediate+.",
    active: true,
    colors: ["white", "blue", "green"],
    url: PODPLAY_EVENTS_URL,
  },
  {
    key: "discounted",
    name: "Discounted open play",
    copy: "Enjoy more time on court at a reduced price. Browse discounted open play by skill group; each scheduled session lists its price and duration.",
    active: true,
    colors: ["white", "blue", "green"],
    url: PODPLAY_EVENTS_URL,
  },
  {
    key: "socials",
    name: "Weekend and late-night socials",
    copy: "Meet people on and off the court with pickleball, board games, video games, and a challenge court. Come any time, leave any time. Participating sessions feature a late-night menu from 8-Bit Bites.",
    active: true,
    colors: ["yellow"],
    url: PODPLAY_EVENTS_URL,
  },
  {
    key: "drill-and-play",
    name: "Social Drill & Play",
    copy: "Bring a partner to practice together and put your skills into games. Both you and your partner must register individually.",
    active: true,
    colors: ["yellow"],
    url: PODPLAY_EVENTS_URL,
  },
  {
    key: "coach-guided",
    name: "Coach Guided",
    copy: "Build confidence with a coach who guides play and offers practical feedback. Practice your skills in games with support throughout the session.",
    active: true,
    colors: ["pink"],
    url: PODPLAY_EVENTS_URL,
  },
];

export const activePrograms = () => PROGRAMS.filter((p) => p.active);

/** Details that belong to one program only. */
export const PROGRAM_NOTES = {
  socials:
    "Weekend challenge-court winners stay on for up to three games. Flexible arrival and departure applies to weekend and late-night socials.",
};

export const FAQ_ADDITIONS: { question: string; answer: string }[] = [
  {
    question: "Do I need a DUPR rating?",
    answer:
      "Some sessions require a minimum rating; others welcome players without one. Check the DUPR requirement on your chosen session. An advertised level is not automatically a signup gate.",
  },
  {
    question: "Are all games submitted to DUPR?",
    answer:
      "Only sessions identified as DUPR-Logged or Earn Your DUPR submit results. A DUPR-gated-only session uses your rating for entry, and games are not submitted.",
  },
  {
    question: "Why are women's sessions pink?",
    answer:
      "Pink identifies women's programming. Look at the session's written level and requirements to choose the right fit.",
  },
];

/** Current women's league series (seasonal; remove after the Dec 5 finale). */
export const MATCHA_AND_MATCHES = {
  name: "Matcha & Matches",
  color: "pink" as CategoryColor,
  format: "Coach Guided",
  year: 2026,
  priceEarlyBird: "$200",
  price: "$220",
  priceNote: "per person for one four-session series",
  timezoneNote: "All times are New York time. No session Thanksgiving week.",
  recommendationNote: "2.7+ is a recommendation only; neither women's league has a rating requirement.",
  showUntil: "2026-12-05",
  image: {
    src: "/leagues/matcha-and-matches.webp",
    srcSet: "/leagues/matcha-and-matches-768.webp 768w, /leagues/matcha-and-matches.webp 1200w",
    alt: "A green matcha and an iced drink on a Velto paddle beside a pickleball on court",
    width: 1200,
    height: 630,
  },
  /** From the current PodPlay league description (27 Oct 2026). */
  included: [
    "Coach-guided play with practical tips and feedback throughout each session.",
    "One handcrafted drink from Gishiki Matcha at every session.",
    "Organized games with rotating partners and opponents within your skill group, and league standings across all four sessions.",
    "Prizes for the top three finishers, including Velto credits and a medal.",
  ],
  days: [
    {
      day: "Thursdays",
      dates: "Nov 5, 12, 19 + Dec 3",
      time: "6–8pm",
      earlyBirdEnds: "Oct 29 at 6pm",
      earlyBirdEndsAt: "2026-10-29T18:00:00-04:00",
      options: [
        {
          level: "Beginner / Advanced Beginner",
          url: "https://velto.podplay.app/community/series/01a179c8-0fdb-7221-b1b9-576e7aba2e85",
        },
        {
          level: "Intermediate 2.7+",
          url: "https://velto.podplay.app/community/series/01a179c3-cce4-7ff8-85f4-7d1a090d587e",
        },
      ],
    },
    {
      day: "Saturdays",
      dates: "Nov 7, 14, 21 + Dec 5",
      time: "10am–noon",
      earlyBirdEnds: "Oct 31 at 10am",
      earlyBirdEndsAt: "2026-10-31T10:00:00-04:00",
      options: [
        {
          level: "Beginner / Advanced Beginner",
          url: "https://velto.podplay.app/community/series/01a179c2-e114-7ff8-85f2-2afdd1df5d60",
        },
        {
          level: "Intermediate 2.7+",
          url: "https://velto.podplay.app/community/series/01a179c3-e3c2-7ff8-85f5-1af26547dd07",
        },
      ],
    },
  ],
};

/** True while a day's early-bird price is still on (New York time in the ISO string). */
export const earlyBirdOpen = (d: { earlyBirdEndsAt: string }, now: number = Date.now()) =>
  now < Date.parse(d.earlyBirdEndsAt);
